import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, ShieldCheck, CheckCircle2, HelpCircle, GraduationCap } from 'lucide-react';
import surveyApi from './surveyApi';
import { useTrainingLang } from '../i18n/TrainingLangContext';

export default function SurveyList() {
  const { language, t } = useTrainingLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const rows = await surveyApi.listSurveys(language);
        if (active) setItems(rows);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load surveys');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [language]);

  if (loading) return <div className="p-8 text-slate-400">{t('loading')}</div>;
  if (error) return <div className="p-8 text-rose-300">{error}</div>;

  return (
    <div className="p-8">
      <div className="flex items-center gap-2 mb-6">
        <ClipboardList className="w-6 h-6 text-amber-400" />
        <h1 className="text-2xl font-bold text-slate-100">{t('navSurveys')}</h1>
      </div>

      {items.length === 0 ? (
        <div className="text-slate-400">{t('surveysEmpty')}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((s) => {
            const responded = s.my_response_count > 0;
            return (
              <div key={s.id} className="rounded-lg bg-slate-800 border border-slate-700 p-5 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="text-lg font-semibold text-slate-100">{s.title}</h2>
                  {responded && (
                    <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-emerald-900/40 text-emerald-300">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {t('surveyDone')}
                    </span>
                  )}
                </div>
                {s.description && (
                  <p className="text-sm text-slate-400 mt-1 line-clamp-3">{s.description}</p>
                )}
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-400">
                  <span className="inline-flex items-center gap-1">
                    <HelpCircle className="w-3.5 h-3.5" /> {t('surveyQuestionCount', { count: s.question_count })}
                  </span>
                  {!!s.is_anonymous && (
                    <span className="inline-flex items-center gap-1 text-emerald-300">
                      <ShieldCheck className="w-3.5 h-3.5" /> {t('surveyAnonymous')}
                    </span>
                  )}
                  {s.related_training_title && (
                    <span className="inline-flex items-center gap-1">
                      <GraduationCap className="w-3.5 h-3.5" /> {s.related_training_title}
                    </span>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-slate-700/60">
                  {s.can_respond ? (
                    <Link to={`/tiles/ksp_training/survey/${s.id}`}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm">
                      {responded ? t('surveyRespondAgain') : t('surveyRespond')}
                    </Link>
                  ) : (
                    <span className="text-sm text-emerald-300 inline-flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" /> {t('surveyResponded')}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
